import { useTranslation } from "react-i18next";
import { useState } from "react";
import { DataGridPro as MuiDataGrid } from "@mui/x-data-grid-pro";
import {
  Grid as MuiGrid,
  Box as MuiBox,
  Paper as MuiPaper,
  Typography as MuiTypography,
  MenuItem as MuiMenuItem,
  Menu as MuiMenu,
  IconButton as MuiIconButton,
  Chip as MuiChip,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { LoadingGridOverlay, NoRowsGridOverlay } from "common";
import {
  AccessTimeOutlined as AccessTimeOutlinedIcon,
  TrendingUpOutlined as TrendingUpOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  MoreHoriz as MoreHorizIcon,
  BarChart as BarChartIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import StudyStatusCard from "app/components/study/mystudies/StudyStatusCard";
import { myStudiesPageStyle } from "app/components/study/mystudies/MyStudiesPageStyle";
import { useStudyContext } from "app/services/myStudyContext";
import { studyTableStyles } from "app/components/study/StudyTableStyles";
import {
  headerStyles,
  multiLineCell,
  tableStyles,
} from "app/shared/UI/sharedStyles";
import {
  convertOncoreLastSyncTimeToDisplayFormat,
  getStatusCategoryForMyStudies,
} from "app/shared/utils";
import { LOADER_TYPES } from "app/shared/constants";
import { Link, useNavigate } from "react-router-dom";

const MyStudiesPageContent = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const {
    studyList,
    totalCount,
    dashboardData,
    oncoreLastSyncTime,
    pagination,
    setPagination,
    sort,
    setSort,
    isLoading,
  } = useStudyContext();

  const handleOpenMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handlePaginationModelChange = (model) => {
    setPagination({
      ...pagination,
      page: model.page,
      rowsPerPage: model.pageSize,
    });
  };

  const handleSortChange = (sortModel) => {
    if (!sortModel.length) return;
    setSort({
      orderBy: sortModel[0].field,
      order: sortModel[0].sort,
    });
  };

  const menuItems = [
    {
      Icon: BarChartIcon,
      label: t("MyStudiesTable.insights"),
      route: "insights",
    } /*
    {
      Icon: HailIcon,
      label: t("MyStudiesTable.prospects"),
      route: "prospects",
    },
    {
      Icon: ScienceIcon,
      label: t("MyStudiesTable.participants"),
      route: "participants",
    },
    {
      Icon: CalendarMonthIcon,
      label: t("MyStudiesTable.visits"),
      route: "visits",
    },
    {
      Icon: MovingIcon,
      label: t("MyStudiesTable.recruitment"),
      route: "recruitment",
    },
    {
      Icon: SettingsIcon,
      label: t("MyStudiesTable.studySettings"),
      route: "settings",
    },
    { 
      Icon: GroupsIcon, 
      label: t("MyStudiesTable.team"), 
      route: "team" 
    },
    {
      Icon: MessageIcon,
      label: t("MyStudiesTable.activity"),
      route: "activity",
    },*/,
    {
      Icon: LanguageIcon,
      label: t("MyStudiesTable.webListing"),
      route: "listingPage",
    },
  ];

  const statusCards = [
    {
      key: "NOT_YET_RECRUITING",
      Icon: AccessTimeOutlinedIcon,
      status: t("MyStudiesTable.notYetRecruiting"),
      color: myStudiesPageStyle.statusCards.notYetRecruitingCard.color,
    },
    {
      key: "RECRUITING",
      Icon: TrendingUpOutlinedIcon,
      status: t("MyStudiesTable.recruiting"),
      color: myStudiesPageStyle.statusCards.recruitingCard.color,
    },
    {
      key: "FINISHED_RECRUITING",
      Icon: CheckCircleOutlineIcon,
      status: t("MyStudiesTable.finishedRecruiting"),
      color: myStudiesPageStyle.statusCards.finishedRecruitingCard.color,
    },
  ];

  const NoStudyOverlay = () => (
    <NoRowsGridOverlay message={t(`common.emptyTableRow`)} />
  );

  const columns = [
    {
      field: "protocolNumber",
      renderHeader: () => (
        <MuiTypography style={headerStyles}>
          {t(`MyStudiesTable.protocolNumber`)}
        </MuiTypography>
      ),
      renderCell: (value) => (
        <MuiTypography sx={multiLineCell}>
          <MuiTypography component="span">
            <Link to={`/studies/${value.row.protocolNumber}/insights`}>
              {value.row.protocolNumber}
            </Link>
          </MuiTypography>

          {value.row.title && (
            <MuiTooltip title={value.row.title} placement="top" arrow>
              <div style={myStudiesPageStyle.ellipsisTextWidth}>
                <span
                  style={{
                    ...myStudiesPageStyle.ellipsisText,
                    ...myStudiesPageStyle.ellipsisTextWidth,
                    ...myStudiesPageStyle.fixedHeight,
                    display: "inline-block",
                  }}
                >
                  {value.row.title}
                </span>
              </div>
            </MuiTooltip>
          )}
        </MuiTypography>
      ),

      minWidth: 100,
      flex: 1,
    },
    {
      field: "irbNumber",
      renderHeader: () => (
        <MuiTypography style={headerStyles}>
          {t(`MyStudiesTable.irb`)}
        </MuiTypography>
      ),
      renderCell: (value) => (
        <MuiTypography sx={multiLineCell}>{value.row.irbNumber}</MuiTypography>
      ),
      minWidth: 100,
      flex: 1,
    },
    {
      field: "nctId",
      renderHeader: () => (
        <MuiTypography style={headerStyles}>
          {t(`MyStudiesTable.nctNumber`)}
        </MuiTypography>
      ),
      renderCell: (value) => (
        <MuiTypography sx={multiLineCell}>{value.row.nctId}</MuiTypography>
      ),
      minWidth: 100,
      flex: 1,
    },
    {
      field: "protocolStatus",
      renderHeader: () => (
        <MuiTypography style={headerStyles}>
          {t(`MyStudiesTable.studyStatus`)}
        </MuiTypography>
      ),
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const status = params.row.protocolStatus;
        const category = getStatusCategoryForMyStudies(status);
        const colors =
          myStudiesPageStyle.statusColors[category] ||
          myStudiesPageStyle.statusColors.UNRECOGNIZED;

        return (
          <MuiChip
            label={t(`MyStudiesTable.status.${status}`)}
            size={t("MyStudiesTable.small")}
            sx={{
              backgroundColor: colors.bg,
              color: colors.text,
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 60,
      renderCell: (params) => (
        <MuiIconButton
          onClick={(e) => params.row.handleOpenMenu(e, params.row)}
        >
          <MoreHorizIcon />
        </MuiIconButton>
      ),
    },
  ];

  return (
    <>
      <MuiBox sx={myStudiesPageStyle.gridWrapper}>
        <MuiGrid container spacing={3}>
          {statusCards.map(({ key, Icon, status, color }) => {
            const count = dashboardData?.[key] ?? 0;
            const label =
              count <= 1
                ? t("MyStudiesTable.study")
                : t("MyStudiesTable.studies");

            return (
              <MuiGrid key={key} size={myStudiesPageStyle.statusCards.gridSize}>
                <StudyStatusCard
                  Icon={Icon}
                  count={count}
                  label={label}
                  status={status}
                  color={color}
                />
              </MuiGrid>
            );
          })}
        </MuiGrid>
      </MuiBox>
      <MuiBox sx={{ mt: 3 }}>
        <MuiTypography sx={{ display: "flex", justifyContent: "flex-end" }}>
          <span style={{ fontWeight: 600 }}>
            <strong>{t("MyStudiesTable.lastUpdated")}</strong>
          </span>
          <span>
            &nbsp;{convertOncoreLastSyncTimeToDisplayFormat(oncoreLastSyncTime)}
          </span>
        </MuiTypography>
        <MuiPaper variant="outlined" sx={studyTableStyles.tableScroll}>
          <MuiDataGrid
            slots={{
              loadingOverlay: LoadingGridOverlay,
              noRowsOverlay: NoStudyOverlay,
            }}
            rows={studyList.map((r) => ({ ...r, handleOpenMenu }))}
            columns={columns}
            autoHeight
            pagination
            disableColumnMenu
            disableColumnResize
            disableColumnReorder
            disableMultipleColumnsSorting
            disableRowSelectionOnClick
            loading={isLoading[LOADER_TYPES.STUDY_DASHBOARD_DATA]}
            paginationMode="server"
            sortingMode="server"
            pageSizeOptions={[5, 10, 20, 50, 100]}
            paginationModel={{
              page: pagination.page,
              pageSize: pagination.rowsPerPage,
            }}
            rowCount={totalCount}
            onPaginationModelChange={handlePaginationModelChange}
            sortModel={[{ field: sort.orderBy, sort: sort.order }]}
            sortingOrder={["asc", "desc"]}
            onSortModelChange={handleSortChange}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? "Mui-even"
                : "Mui-odd"
            }
            sx={tableStyles}
            getRowHeight={() => "auto"}
          />
        </MuiPaper>
      </MuiBox>

      <MuiMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 220,
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        {menuItems.map((item, index) => (
          <MuiMenuItem
            key={index}
            onClick={() =>
              navigate(`/studies/${selectedRow?.protocolNumber}/${item.route}`)
            }
          >
            <item.Icon fontSize={t("MyStudiesTable.small")} sx={{ mr: 1 }} />
            {item.label}
          </MuiMenuItem>
        ))}
      </MuiMenu>
    </>
  );
};

export default MyStudiesPageContent;
